import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  usePsaPicklists,
  useUpdatePsaConnection,
  type HaloConfig,
  type Picklist,
  type PsaConnection,
  type PriorityMap,
} from '@/hooks/usePsa.ts';
import { apiErrorMessage } from '@/utils/apiError.ts';
import { PicklistSelect } from './PicklistSelect.tsx';
import { picklistOptions as options, SEVERITIES } from './psaSettings.ts';
import { Button } from '@/components/ui/button.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';

/**
 * Halo's settings for a connection, offered from Halo's own lists
 * (docs/PsaIntegration.md, "The neutral layer and the PSA operations").
 *
 * Every value is stored as Halo's ID and shown by its name. Unlike Autotask, Halo
 * fills in what's left unset from the ticket type and the SLA, so only the note
 * outcome is needed: without one, no note can be written.
 */
export function HaloSettings({ connection }: { connection: PsaConnection }) {
  const picklists = usePsaPicklists(connection.id, true);
  if (picklists.isLoading) {
    return (
      <p className="text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Reading Halo’s lists…
      </p>
    );
  }
  if (picklists.isError) {
    return (
      <p className="text-sm text-destructive">{apiErrorMessage(picklists.error, 'Couldn’t read Halo’s lists.')}</p>
    );
  }
  return <HaloSettingsForm connection={connection} picklists={picklists.data ?? []} />;
}

function HaloSettingsForm({ connection, picklists }: { connection: PsaConnection; picklists: Picklist[] }) {
  // Notes are hidden from the end user unless the admin chooses otherwise (decision 4).
  const [draft, setDraft] = useState<HaloConfig>(() => ({ hidden_from_user: true, ...connection.config }));
  const update = useUpdatePsaConnection(connection.id);
  const set = (patch: Partial<HaloConfig>) => { setDraft((d) => ({ ...d, ...patch })); };
  const priorities = draft.priority_map ?? {};
  const statuses = options(picklists, 'status_id');
  const ackIds = draft.ack_status_ids ?? [];
  const field = (name: string) => `psa-${connection.id}-${name}`;

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate({ config: draft });
      }}
    >
      <fieldset className="space-y-3">
        <legend className="text-sm font-medium mb-1">New tickets</legend>
        <PicklistSelect id={field('type')} label="Ticket type" choices={options(picklists, 'tickettype_id')}
          value={draft.tickettype_id} onChange={(v) => { set({ tickettype_id: v }); }} noneLabel="Halo’s default" />
        <PicklistSelect id={field('status')} label="Status" choices={statuses}
          value={draft.new_status_id} onChange={(v) => { set({ new_status_id: v }); }}
          noneLabel="The ticket type’s own" />
        <PicklistSelect id={field('team')} label="Team" choices={options(picklists, 'team_id')}
          value={draft.team_id} onChange={(v) => { set({ team_id: v }); }} noneLabel="None" />
        <PicklistSelect id={field('agent')} label="Agent" choices={options(picklists, 'agent_id')}
          value={draft.agent_id} onChange={(v) => { set({ agent_id: v }); }} noneLabel="Unassigned" />
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium mb-1">Priority for each severity</legend>
        <p className="text-xs text-muted-foreground">
          Severity sets the ticket’s priority, one way: a priority changed in Halo isn’t copied back. Halo numbers
          priorities within each SLA, so the ticket’s SLA decides which of its priorities each one is.
        </p>
        {SEVERITIES.map((s) => (
          <PicklistSelect key={s.key} id={field(`priority-${s.key}`)} label={s.label}
            choices={options(picklists, 'priority_id')} value={priorities[s.key]} noneLabel="Halo’s default"
            onChange={(v) => {
              const next: PriorityMap = Object.fromEntries(
                Object.entries(priorities).filter(([key]) => key !== s.key),
              );
              if (v != null) next[s.key] = v;
              set({ priority_map: next });
            }} />
        ))}
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium mb-1">Status changes</legend>
        <div className="space-y-1">
          <span className="text-sm font-medium">Statuses that mean someone has it</span>
          <p className="text-xs text-muted-foreground">
            Acknowledging the incident in Witina moves the ticket to the first one ticked.
          </p>
          <div className="grid grid-cols-2 gap-2 pt-1">
            {statuses.filter((o) => o.active).map((o) => {
              const id = Number(o.value);
              const checked = ackIds.includes(id);
              return (
                <label key={o.value} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={checked}
                    onCheckedChange={(on) => {
                      set({ ack_status_ids: on === true ? [...ackIds, id] : ackIds.filter((x) => x !== id) });
                    }} />
                  {o.label}
                </label>
              );
            })}
          </div>
        </div>
        <PicklistSelect id={field('recovery')} label="When the network recovers" choices={statuses}
          value={draft.recovery_status_id} onChange={(v) => { set({ recovery_status_id: v }); }}
          noneLabel="Add a note, leave the status alone"
          help="Witina says when the network is healthy; the ticket says when the work is done." />
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium mb-1">Notes</legend>
        <PicklistSelect id={field('outcome')} label="Note outcome" choices={options(picklists, 'outcome_id')}
          value={draft.note_outcome_id} onChange={(v) => { set({ note_outcome_id: v }); }}
          help="The action Witina’s notes are added as." />
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={draft.hidden_from_user !== false}
            onCheckedChange={(on) => { set({ hidden_from_user: on === true }); }} />
          Hide Witina’s notes from the end user
        </label>
        <p className="text-xs text-muted-foreground">
          On unless you choose otherwise: notes carry network detail and text devices report.
        </p>
      </fieldset>

      {update.isError ? (
        <p className="text-sm text-destructive">{apiErrorMessage(update.error, 'Couldn’t save the settings.')}</p>
      ) : null}
      {update.isSuccess ? <p className="text-sm text-muted-foreground">Saved.</p> : null}
      <Button type="submit" size="sm" disabled={update.isPending}>
        {update.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        Save settings
      </Button>
      {draft.note_outcome_id == null ? (
        <p className="text-xs text-muted-foreground">Choose a note outcome before Witina can add notes to tickets.</p>
      ) : null}
    </form>
  );
}
